import type { VaultChange, VaultChangeChannel } from '../types.js'

/**
 * Two-tab notification over BroadcastChannel. Revision checks are the
 * authority for conflicts; this channel only tells other tabs that a record
 * changed so they can refresh (LOC-03). Falls back to a no-op where absent.
 */

export interface BroadcastChannelLike {
  postMessage(message: unknown): void
  close(): void
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void
  removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void
}

type BroadcastChannelConstructor = new (name: string) => BroadcastChannelLike

export function createBroadcastVaultChangeChannel(name: string): VaultChangeChannel {
  const Constructor = (globalThis as { BroadcastChannel?: BroadcastChannelConstructor })
    .BroadcastChannel
  if (Constructor === undefined) {
    return {
      publish() {},
      subscribe() {
        return () => {}
      },
      close() {},
    }
  }

  const channel = new Constructor(name)
  return {
    publish(change: VaultChange): void {
      channel.postMessage(change)
    },
    subscribe(listener: (change: VaultChange) => void): () => void {
      const handler = (event: { data: unknown }): void => {
        listener(event.data as VaultChange)
      }
      channel.addEventListener('message', handler)
      return () => channel.removeEventListener('message', handler)
    },
    close(): void {
      channel.close()
    },
  }
}
